import React, {useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {requestPasswordReset} from '../store/slices/authThunks';
import {requestPasswordResetApiSelector} from '../store/selectors/auth.selectors';
import AuthCard from './auth/AuthCard';
import RequestResetForm from './auth/RequestResetForm';
import {CheckIcon} from './Icons';

const BackToSignIn = () => (
  <Link to="/login" className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
        ← Back to sign in
  </Link>
);

/**
 * Step 1 of the password reset: ask for the address, then email a magic link.
 *
 * The confirmation is shown unconditionally, regardless of whether the address has an account —
 * reacting differently would tell an attacker which addresses are registered.
 */
const ForgotPasswordContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const requestApi = useAppSelector(requestPasswordResetApiSelector);

  // Carried over from the sign-in screen so the user does not retype what they just typed.
  const initialEmail = searchParams.get('email') || '';

  const [sentTo, setSentTo] = useState<string | null>(null);

  const handleSubmit = async (email: string) => {
    try {
      await dispatch(requestPasswordReset(email)).unwrap();
      setSentTo(email);
    } catch {
      // Rate limiting is the only rejection the thunk lets through; the card renders it.
    }
  };

  if (sentTo) {
    return (
      <AuthCard
        title="Check your email"
        subtitle="If that account exists, a reset link is on its way."
        footer={<BackToSignIn />}
      >
        <div className="mt-5 flex items-start gap-2 rounded-lg border border-emerald-500/30
         bg-emerald-500/[0.06] px-3 py-2.5 text-sm text-emerald-200">
          <CheckIcon className="mt-0.5 w-4 h-4 shrink-0" />
          <span>
                        If an account exists for <span className="font-medium">{sentTo}</span>, we've just emailed a
                        link to reset the password. Click it to set a new one.
          </span>
        </div>
        <p className="mt-3 text-xs text-gray-600">
                    No email after a few minutes? That usually just means there&apos;s no account with that address
                    — double-check for typos, or try signing up instead.
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Reset your password"
      subtitle="We'll email you a link to set a new one."
      error={requestApi.error}
      footer={<BackToSignIn />}
    >
      <RequestResetForm
        initialEmail={initialEmail}
        submitting={requestApi.status === 'pending'}
        onSubmit={handleSubmit}
      />
    </AuthCard>
  );
};

export default ForgotPasswordContainer;
